import React, { useEffect, useRef, useImperativeHandle, forwardRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const FastagDashboardMap = forwardRef((
  { vehicleDataMap,vehicles, selectedVehicleNo, selectedTransaction, onVehicleSelect, onTransactionSelect },
  ref
) => {
  const navigate = useNavigate();
  const mapRef         = useRef(null);
  const mapInstanceRef = useRef(null);
  const olaMapsRef     = useRef(null);
  const markersRef     = useRef([]);
  const mapReadyRef    = useRef(false);
  const pendingDrawRef = useRef(false);

  // Keep latest props accessible inside async callbacks
  const vehicleDataMapRef      = useRef(vehicleDataMap);
  const selectedVehicleNoRef   = useRef(selectedVehicleNo);
  const selectedTransactionRef = useRef(selectedTransaction);
  vehicleDataMapRef.current      = vehicleDataMap;
  selectedVehicleNoRef.current   = selectedVehicleNo;
  selectedTransactionRef.current = selectedTransaction;
  const vehiclesRef = useRef(vehicles);
  vehiclesRef.current = vehicles;

  /* ── Helpers ── */
  const parseGeocode = (geocode) => {
    if (!geocode) return null;
    const parts = geocode.split(",").map((s) => parseFloat(s.trim()));
    if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
    return [parts[1], parts[0]]; // lat,lng → [lng,lat]
  };

  /* ── Truck SVG shared ── */
  const truckSVG = (bg) => `
    <svg viewBox="0 0 32 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="width:20px;height:15px;display:block">
      <rect x="1" y="5" width="20" height="14" rx="2" fill="white" opacity="0.95"/>
      <path d="M21 9h5l4 6v5h-9V9z" fill="white" opacity="0.95"/>
      <circle cx="7"  cy="20" r="2.5" fill="${bg}" stroke="white" stroke-width="1.5"/>
      <circle cx="24" cy="20" r="2.5" fill="${bg}" stroke="white" stroke-width="1.5"/>
    </svg>
  `;

  /* ── Vehicle marker: used in multi-vehicle mode ── */
  const buildVehicleMarkerEl = useCallback((vehicleRegNo, isSelected, vendorName, vendorCode) => {
    const bg = isSelected ? "#d97706" : "#0f172a";

    const wrapper = document.createElement("div");
    wrapper.style.cssText = "display:flex;flex-direction:column;align-items:center;cursor:pointer;user-select:none;";

    const box = document.createElement("div");
    box.style.cssText = `
      width:36px;height:36px;border-radius:8px;
      background:${bg};border:2px solid ${isSelected ? "#d97706" : "#334155"};
      display:flex;align-items:center;justify-content:center;
      box-shadow:0 2px 8px rgba(0,0,0,0.22);
      transition:transform 0.15s,box-shadow 0.15s;
    `;
    box.innerHTML = truckSVG(bg);

    const nameTag = document.createElement("div");
    nameTag.style.cssText = `
      margin-top:3px;padding:1px 6px;border-radius:3px;
      font-size:8px;font-weight:700;font-family:monospace;
      background:rgba(15,23,42,0.82);color:#f8fafc;
      white-space:nowrap;max-width:120px;overflow:hidden;text-overflow:ellipsis;
      letter-spacing:0.04em;
    `;
    nameTag.innerText = vehicleRegNo;

    wrapper.appendChild(box);
    wrapper.appendChild(nameTag);

    let vendorTag = null;
    if (vendorName) {
      vendorTag = document.createElement("div");
      vendorTag.style.cssText = `
        display:none;margin-top:2px;padding:2px 7px;border-radius:99px;
        font-size:8px;font-weight:600;font-family:sans-serif;
        background:#dbeafe;color:#1d4ed8;
        white-space:nowrap;max-width:130px;overflow:hidden;text-overflow:ellipsis;
        cursor:pointer;border:1px solid #bfdbfe;
      `;
      vendorTag.innerText = vendorName;
      vendorTag.onclick = (e) => {
        e.stopPropagation();
        navigate(`/admin/vendors?vendorCode=${vendorCode}`);
      };
      wrapper.appendChild(vendorTag);
    }
  
    wrapper.onmouseenter = () => { box.style.transform = "scale(1.2)"; box.style.boxShadow = "0 4px 16px rgba(0,0,0,0.3)"; };
    wrapper.onmouseleave = () => { box.style.transform = "scale(1)";   box.style.boxShadow = "0 2px 8px rgba(0,0,0,0.22)"; };

    return wrapper;
  }, []);

  /* ── Route stop marker: used in single-vehicle route mode ── */
  const buildRouteMarkerEl = useCallback(({ isFirst, isLast, index, isSelected, txn }) => {
    const bg         = isFirst ? "#059669" : isLast ? "#0369a1" : isSelected ? "#d97706" : "#0f172a";
    const border     = isFirst ? "#059669" : isLast ? "#0369a1" : isSelected ? "#d97706" : "#334155";
    const label      = isFirst ? "S"       : isLast ? "E"       : String(index + 1);
    const labelBg    = isFirst ? "#d1fae5" : isLast ? "#bae6fd" : isSelected ? "#fef3c7" : "#f1f5f9";
    const labelColor = isFirst ? "#065f46" : isLast ? "#075985" : isSelected ? "#92400e" : "#475569";

    const wrapper = document.createElement("div");
    wrapper.style.cssText = "display:flex;flex-direction:column;align-items:center;cursor:pointer;user-select:none;";

    const box = document.createElement("div");
    box.style.cssText = `
      width:36px;height:36px;border-radius:8px;
      background:${bg};border:2px solid ${border};
      display:flex;align-items:center;justify-content:center;
      box-shadow:0 2px 8px rgba(0,0,0,0.22);
      transition:transform 0.15s,box-shadow 0.15s;
      position:relative;
    `;
    box.innerHTML = `
      ${truckSVG(bg)}
      <span style="
        position:absolute;top:-7px;right:-7px;
        min-width:16px;height:16px;border-radius:99px;
        background:${labelBg};color:${labelColor};
        font-size:8px;font-weight:700;font-family:sans-serif;
        display:flex;align-items:center;justify-content:center;
        padding:0 3px;border:1.5px solid white;
        box-shadow:0 1px 3px rgba(0,0,0,0.15);
      ">${label}</span>
    `;

    const nameTag = document.createElement("div");
    nameTag.style.cssText = `
      margin-top:3px;padding:1px 5px;border-radius:3px;
      font-size:8px;font-weight:600;font-family:sans-serif;
      background:rgba(15,23,42,0.78);color:#f8fafc;
      white-space:nowrap;max-width:110px;overflow:hidden;text-overflow:ellipsis;
    `;
    nameTag.innerText = txn.tollPlazaName || "";

    wrapper.appendChild(box);
    wrapper.appendChild(nameTag);
    wrapper.onmouseenter = () => { box.style.transform = "scale(1.2)"; box.style.boxShadow = "0 4px 16px rgba(0,0,0,0.3)"; };
    wrapper.onmouseleave = () => { box.style.transform = "scale(1)";   box.style.boxShadow = "0 2px 8px rgba(0,0,0,0.22)"; };

    return wrapper;
  }, []);

  /* ── Route polyline helpers ── */
  const clearRoute = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    try {
      if (map.getLayer("db-route-line"))   map.removeLayer("db-route-line");
      if (map.getLayer("db-route-shadow")) map.removeLayer("db-route-shadow");
      if (map.getSource("db-route"))       map.removeSource("db-route");
    } catch (_) {}
  }, []);

  const drawRoutePolyline = useCallback((coords) => {
    const map = mapInstanceRef.current;
    if (!map || !coords.length) return;
    clearRoute();
    map.addSource("db-route", { type: "geojson", data: { type: "Feature", geometry: { type: "LineString", coordinates: coords } } });
    map.addLayer({ id: "db-route-shadow", type: "line", source: "db-route", layout: { "line-join": "round", "line-cap": "round" }, paint: { "line-color": "#000", "line-width": 5, "line-opacity": 0.07, "line-translate": [0, 2] } });
    map.addLayer({ id: "db-route-line",   type: "line", source: "db-route", layout: { "line-join": "round", "line-cap": "round" }, paint: { "line-color": "#d97706", "line-width": 3, "line-opacity": 0.85, "line-dasharray": [2, 1.5] } });
  }, [clearRoute]);

  /* ── Core draw function ── */
  const draw = useCallback((vDataMap, selVehicleNo, selTxn) => {
    const map     = mapInstanceRef.current;
    const olaMaps = olaMapsRef.current;
    if (!map || !olaMaps || !mapReadyRef.current) return;

    // Clear markers + route
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];
    clearRoute();

    if (!vDataMap || !Object.keys(vDataMap).length) return;

    if (selVehicleNo) {
      /* ── SINGLE VEHICLE ROUTE MODE ── */
      const txns   = vDataMap[selVehicleNo] || [];
      const sorted = [...txns].sort((a, b) => new Date(a.readerReadTime) - new Date(b.readerReadTime));
      const coords = sorted.map((t) => parseGeocode(t.tollPlazaGeocode)).filter(Boolean);
      if (!coords.length) return;

      drawRoutePolyline(coords);

      sorted.forEach((txn, index) => {
        const lngLat = parseGeocode(txn.tollPlazaGeocode);
        if (!lngLat) return;

        const el = buildRouteMarkerEl({
          isFirst: index === 0,
          isLast:  index === sorted.length - 1,
          index,
          isSelected: selTxn?.seqNo === txn.seqNo,
          txn,
        });

        el.onclick = (e) => { e.stopPropagation(); onTransactionSelect?.(txn); };

        const marker = olaMaps.addMarker({ element: el, anchor: "bottom" }).setLngLat(lngLat).addTo(map);
        markersRef.current.push(marker);
      });

    } else {
      /* ── MULTI VEHICLE MODE — one marker per vehicle at latest toll ── */
      const entries = Object.entries(vDataMap).filter(([, txns]) => txns?.length);
      const vehicleList = vehiclesRef.current || [];

        entries.forEach(([vehicleRegNo, txns], idx) => {
        const raw = parseGeocode(txns[0].tollPlazaGeocode);
        if (!raw) return;

        // Each vehicle gets a unique offset based on its index
        // regardless of how many share the same geocode
        const angle  = idx * 137.5 * (Math.PI / 180); // golden angle — spreads evenly
        const ring   = Math.floor(idx / 8);            // new ring every 8 vehicles
        const radius = idx === 0 ? 0 : 0.015 * (ring + 1);
        const lngLat = [
        raw[0] + radius * Math.cos(angle),
        raw[1] + radius * Math.sin(angle),
        ];

        const vehicleMeta = vehicleList.find((v) => v.vehicleRegNo === vehicleRegNo);

        const el = buildVehicleMarkerEl(vehicleRegNo, false, vehicleMeta?.vendorName, vehicleMeta?.vendorCode);
        el.onclick = (e) => { e.stopPropagation(); onVehicleSelect?.(vehicleRegNo); };

        const marker = olaMaps.addMarker({ element: el, anchor: "bottom" }).setLngLat(lngLat).addTo(map);
        markersRef.current.push(marker);
    });
}
  }, [buildVehicleMarkerEl, buildRouteMarkerEl, drawRoutePolyline, clearRoute, onVehicleSelect, onTransactionSelect]);

  /* ── Imperative API ── */
  useImperativeHandle(ref, () => ({
    focusOnRoute: (txns) => {
      const map = mapInstanceRef.current;
      if (!map || !txns?.length) return;
      const coords = txns.map((t) => parseGeocode(t.tollPlazaGeocode)).filter(Boolean);
      if (!coords.length) return;
      if (coords.length === 1) { map.flyTo({ center: coords[0], zoom: 12, duration: 1000 }); return; }
      let minLng = 180, maxLng = -180, minLat = 90, maxLat = -90;
      coords.forEach(([lng, lat]) => { minLng = Math.min(minLng, lng); maxLng = Math.max(maxLng, lng); minLat = Math.min(minLat, lat); maxLat = Math.max(maxLat, lat); });
      map.fitBounds(
        [[minLng, minLat], [maxLng, maxLat]], 
        { padding: 80, maxZoom: 10, duration: 1000 }
    );
    },
    focusOnAllVehicles: (vDataMap) => {
      const map = mapInstanceRef.current;
      if (!map) return;
      const coords = Object.values(vDataMap)
        .map((txns) => parseGeocode(txns?.[0]?.tollPlazaGeocode))
        .filter(Boolean);
      if (!coords.length) return;
      if (coords.length === 1) { 
        map.flyTo({ center: coords[0], zoom: 6, duration: 1000 }); 
        return; 
    }
      let minLng = 180, maxLng = -180, minLat = 90, maxLat = -90;
      coords.forEach(([lng, lat]) => { 
        minLng = Math.min(minLng, lng); 
        maxLng = Math.max(maxLng, lng); 
        minLat = Math.min(minLat, lat); 
        maxLat = Math.max(maxLat, lat); });
      map.fitBounds(
        [[minLng, minLat], [maxLng, maxLat]], 
        { padding: 120, maxZoom: 6, duration: 1000 }
    );
    },
    focusOnTransaction: (txn) => {
      const map = mapInstanceRef.current;
      if (!map || !txn) return;
      const coords = parseGeocode(txn.tollPlazaGeocode);
      if (coords) map.flyTo({ center: coords, zoom: 14, duration: 1000 });
    },
    flyToCenter: ([lng, lat], zoom = 7) => {
    const map = mapInstanceRef.current;
    if (!map) return;
    map.flyTo({ center: [lng, lat], zoom, duration: 1000 });
  },
  }));

  /* ── Init map (runs once) ── */
  useEffect(() => {
    let mounted = true;
    const loadMap = async () => {
      const { OlaMaps } = await import("olamaps-web-sdk");
      if (!mounted) return;
      const olaMaps = new OlaMaps({ apiKey: process.env.REACT_APP_OLA_API_KEY });
      const map = olaMaps.init({
        container: mapRef.current,
        style: "https://api.olamaps.io/tiles/vector/v1/styles/default-light-standard/style.json",
        center: [77.23, 28.61],
        zoom: 5,
      });
      mapInstanceRef.current = map;
      olaMapsRef.current     = olaMaps;
      map.on("load", () => {
        if (!mounted) return;
        mapReadyRef.current = true;
        if (pendingDrawRef.current) {
          pendingDrawRef.current = false;
          draw(vehicleDataMapRef.current, selectedVehicleNoRef.current, selectedTransactionRef.current);
        }
      });
    };
    loadMap();
    return () => { mounted = false; };
  }, []);

  /* ── Redraw on prop changes ── */
  useEffect(() => {
    if (!mapReadyRef.current) {
      if (vehicleDataMap && Object.keys(vehicleDataMap).length) pendingDrawRef.current = true;
      return;
    }
    draw(vehicleDataMap, selectedVehicleNo, selectedTransaction);
  }, [vehicleDataMap, vehicles, selectedVehicleNo, selectedTransaction, draw]);

  return <div ref={mapRef} style={{ width: "100%", height: "100%", flex: 1 }} />;
});

FastagDashboardMap.displayName = "FastagDashboardMap";
export default FastagDashboardMap;